import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Users, RefreshCw, Copy, Check, Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';

// Get customer app domain from environment
const CUSTOMER_APP_DOMAIN = process.env.REACT_APP_REFERRAL_DOMAIN || window.location.origin;

const ReferralRegistrationsPage = () => {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const qrRefs = useRef({});

  useEffect(() => {
    fetchRegistrations();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredRegistrations(registrations);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredRegistrations(
        registrations.filter(
          (reg) =>
            reg.name?.toLowerCase().includes(query) ||
            reg.phone?.includes(query) ||
            reg.referral_code?.toLowerCase().includes(query) ||
            reg.user_id?.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, registrations]);

  const fetchRegistrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/admin/referral-registrations`, { withCredentials: true });
      setRegistrations(response.data);
      setFilteredRegistrations(response.data);
    } catch (err) {
      console.error('Failed to load referral registrations:', err);
      if (err.response?.status === 403) {
        setError('Admin access required to view referral registrations.');
      } else if (err.response?.status === 401) {
        setError('Please login to access this page.');
      } else {
        setError('Unable to load referral registrations. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, id, type) => {
    navigator.clipboard.writeText(text);
    setCopiedId(`${id}-${type}`);
    toast.success(`${type} copied!`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadQR = (referralCode) => {
    const canvas = qrRefs.current[referralCode];
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${referralCode}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('QR code downloaded!');
    }
  };

  const handleCopyQR = async (referralCode) => {
    const canvas = qrRefs.current[referralCode];
    if (canvas) {
      try {
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (blob && navigator.clipboard && navigator.clipboard.write) {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          setCopiedId(`${referralCode}-QR`);
          toast.success('QR code copied!');
          setTimeout(() => setCopiedId(null), 2000);
        } else {
          toast.info('Copy not supported, please download.');
        }
      } catch (err) {
        toast.info('Copy not supported, please download.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-white/20 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Referral Registrations</h1>
                <p className="text-purple-200 text-sm">Users who joined the referral program</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search and Refresh */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search by name, phone, or referral code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
              data-testid="search-referrals"
            />
          </div>
          <Button
            onClick={fetchRegistrations}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
              <span className="ml-3 text-gray-600">Loading referral registrations...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 mb-4">{error}</p>
              <Button onClick={fetchRegistrations} variant="outline">
                Retry
              </Button>
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No referral registrations found.</p>
              {searchQuery && (
                <p className="text-gray-400 text-sm mt-2">Try adjusting your search query.</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      User ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      User Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Login Phone Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Referral Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Referral Link
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      QR Code
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRegistrations.map((reg, index) => (
                    <tr
                      key={reg.user_id || index}
                      className="hover:bg-gray-50 transition-colors"
                      data-testid={`referral-row-${index}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 font-mono">{reg.user_id?.slice(0, 8)}...</span>
                          {reg.user_id && (
                            <button
                              onClick={() => handleCopy(reg.user_id, reg.user_id, 'User ID')}
                              className="p-1 hover:bg-gray-100 rounded"
                              title="Copy User ID"
                              data-testid={`copy-userid-${reg.user_id}`}
                            >
                              {copiedId === `${reg.user_id}-User ID` ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-400" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{reg.name || 'User'}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">{reg.phone || '-'}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-sm font-semibold rounded">
                            {reg.referral_code}
                          </span>
                          <button
                            onClick={() => handleCopy(reg.referral_code, reg.user_id, 'Code')}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Copy referral code"
                            data-testid={`copy-code-${reg.user_id}`}
                          >
                            {copiedId === `${reg.user_id}-Code` ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-sm text-blue-600 underline cursor-pointer hover:text-blue-800 truncate max-w-[200px] md:max-w-none"
                            onClick={() => navigate(`/admin/referral-registrations/${reg.referral_code}`)}
                            title={`${CUSTOMER_APP_DOMAIN}/r/${reg.referral_code}`}
                          >
                            {`${CUSTOMER_APP_DOMAIN}/r/${reg.referral_code}`}
                          </span>
                          <button
                            onClick={() => handleCopy(`${CUSTOMER_APP_DOMAIN}/r/${reg.referral_code}`, reg.user_id, 'Link')}
                            className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
                            title="Copy referral link"
                            data-testid={`copy-link-${reg.user_id}`}
                          >
                            {copiedId === `${reg.user_id}-Link` ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div
                            onClick={() => navigate(`/admin/referral-registrations/${reg.referral_code}`)}
                            title="View referral performance"
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            <QRCodeCanvas
                              value={`${CUSTOMER_APP_DOMAIN}/r/${reg.referral_code}`}
                              size={48}
                              level="M"
                              includeMargin={false}
                            />
                          </div>
                          {/* Hidden large QR for download/copy */}
                          <div className="hidden">
                            <div ref={(el) => { if (el) qrRefs.current[reg.referral_code] = el?.querySelector('canvas'); }}>
                              <QRCodeCanvas
                                value={`${CUSTOMER_APP_DOMAIN}/r/${reg.referral_code}`}
                                size={200}
                                level="H"
                                includeMargin={true}
                              />
                            </div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => handleDownloadQR(reg.referral_code)}
                              className="p-1 hover:bg-gray-100 rounded flex items-center gap-1 text-xs text-gray-600"
                              title="Download QR"
                              data-testid={`download-qr-${reg.user_id}`}
                            >
                              <Download className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleCopyQR(reg.referral_code)}
                              className="p-1 hover:bg-gray-100 rounded flex items-center gap-1 text-xs text-gray-600"
                              title="Copy QR"
                              data-testid={`copy-qr-${reg.user_id}`}
                            >
                              {copiedId === `${reg.referral_code}-QR` ? (
                                <Check className="w-3 h-3 text-green-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          Showing {filteredRegistrations.length} of {registrations.length} referral participants
        </div>
      </div>
    </div>
  );
};

export default ReferralRegistrationsPage;
